/**
 * PlaybookListItem Component
 *
 * Presentational component for rendering a playbook in a list with actions.
 * Stateless - receives all data and callbacks via props.
 * Reusable across desktop and mobile layouts.
 *
 * Uses Card + CardBody pattern for consistency with LinkedCriteriaList,
 * LinkedConfluencesDualList, and LinkedEntryModelsList.
 */

import React from 'react';
import { MoreVertical } from 'lucide-react';

import {
  Button,
  Card,
  CardBody,
  Chip,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@enfomentis/ui';
import type { Playbook } from '@/types/schema/Playbook';

import { SharedIndicator } from './SharedIndicator';

export interface PlaybookListItemProps {
  playbook: Playbook;
  isSelected: boolean;
  isArchived: boolean;
  onSelect: () => void;
  /** Strategy names that contain this playbook (for shared indicator) */
  sharedStrategyNames?: string[] | undefined;
  /** Whether to show the actions popover button (defaults to true) */
  showActions?: boolean;
  /** Called when user clicks "Duplicate" (copy within current strategy) */
  onDuplicate?: () => void;
  /** Called when user clicks "Copy to Strategy..." (opens strategy picker) */
  onCopyToStrategy?: () => void;
  /** Called when user clicks "Move to Strategy" */
  onMove?: () => void;
  /** Called when user clicks "Share with Strategy..." */
  onShare?: () => void;
  /** Called when user clicks "Rename" (mobile only) */
  onRename?: () => void;
  /** Required when showActions is true */
  onUnlink?: () => void;
  /** Called when user clicks "Archive" (only shown when not archived) */
  onArchive?: (() => void) | undefined;
  /** Called when user clicks "Restore" (only shown when archived) */
  onRestore?: (() => void) | undefined;
  /** Required when showActions is true */
  isActionsDisabled?: boolean;
  /** Required when showActions is true */
  showActionsPopover?: boolean;
  /** Required when showActions is true */
  onActionsPopoverChange?: (open: boolean) => void;
}

// No-op function for optional callbacks
const noop = () => {};

export const PlaybookListItem: React.FC<PlaybookListItemProps> = ({
  playbook,
  isSelected,
  isArchived,
  onSelect,
  sharedStrategyNames,
  showActions = true,
  onDuplicate,
  onCopyToStrategy,
  onMove,
  onShare,
  onRename,
  onUnlink = noop,
  onArchive,
  onRestore,
  isActionsDisabled = false,
  showActionsPopover = false,
  onActionsPopoverChange = noop,
}) => {
  // Handle keyboard activation (Enter/Space) for accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault(); // Prevent Space from scrolling the page
      onSelect();
    }
  };

  return (
    <Card radius="sm" shadow="none" className="border border-default-200">
      <CardBody className="p-0">
        <div
          role="button"
          tabIndex={0}
          onClick={onSelect}
          onKeyDown={handleKeyDown}
          className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors ${
            isSelected
              ? 'bg-primary-100 dark:bg-primary-900/30'
              : 'hover:bg-default-100'
          }`}
        >
          {/* Name */}
          <span
            className={`min-w-0 flex-1 truncate font-medium text-sm ${
              isArchived ? 'text-text-tertiary' : 'text-text-primary'
            }`}
          >
            {playbook.name}
          </span>

          {/* Shared Indicator */}
          {sharedStrategyNames && sharedStrategyNames.length >= 2 && (
            <SharedIndicator strategyNames={sharedStrategyNames} />
          )}

          {/* Archived Badge */}
          {isArchived && (
            <Chip className="shrink-0 text-xs text-text-tertiary bg-default-100">
              Archived
            </Chip>
          )}

          {/* Actions Popover - now inside the row */}
          {showActions && (
            <Popover
              isOpen={showActionsPopover}
              onOpenChange={onActionsPopoverChange}
              placement="bottom-end"
            >
              <PopoverTrigger>
                <Button
                  size="sm"
                  variant="light"
                  isIconOnly
                  onClick={(e) => e.stopPropagation()}
                  title="Playbook actions"
                  aria-label="Playbook actions"
                  className="shrink-0 opacity-60 hover:opacity-100"
                >
                  <MoreVertical size={16} />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-1">
                <div className="flex flex-col min-w-40">
                  {onRename && (
                    <Button
                      size="md"
                      variant="light"
                      className="justify-start"
                      isDisabled={isActionsDisabled}
                      onPress={onRename}
                    >
                      Rename
                    </Button>
                  )}
                  {onDuplicate && (
                    <Button
                      size="md"
                      variant="light"
                      className="justify-start"
                      isDisabled={isActionsDisabled}
                      onPress={onDuplicate}
                    >
                      Duplicate
                    </Button>
                  )}
                  {onCopyToStrategy && (
                    <Button
                      size="md"
                      variant="light"
                      className="justify-start"
                      isDisabled={isActionsDisabled}
                      onPress={onCopyToStrategy}
                    >
                      Copy to Strategy...
                    </Button>
                  )}
                  {onMove && (
                    <Button
                      size="md"
                      variant="light"
                      className="justify-start"
                      isDisabled={isActionsDisabled}
                      onPress={onMove}
                    >
                      Move to Strategy...
                    </Button>
                  )}
                  {onShare && (
                    <Button
                      size="md"
                      variant="light"
                      className="justify-start"
                      isDisabled={isActionsDisabled}
                      onPress={onShare}
                    >
                      Share with Strategy...
                    </Button>
                  )}
                  <Button
                    size="md"
                    variant="light"
                    className="justify-start"
                    isDisabled={isActionsDisabled}
                    onPress={onUnlink}
                  >
                    Unlink from Strategy
                  </Button>
                  {onArchive && !isArchived && (
                    <Button
                      size="md"
                      variant="light"
                      className="justify-start text-warning-600"
                      isDisabled={isActionsDisabled}
                      onPress={onArchive}
                    >
                      Archive
                    </Button>
                  )}
                  {onRestore && isArchived && (
                    <Button
                      size="md"
                      variant="light"
                      className="justify-start text-success-600"
                      isDisabled={isActionsDisabled}
                      onPress={onRestore}
                    >
                      Restore
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </CardBody>
    </Card>
  );
};
